
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { getFrontFileUrl } from "../../services/apiFront";
import { fetchEgliseInfo, fetchAspirantsByEglise } from "../../services/aspirantService";

export default function MemoiresEglise() {
  const [memoires, setMemoires] = useState([]);
  const [aspirants, setAspirants] = useState([]);
  const [selectedAspirantId, setSelectedAspirantId] = useState("");
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    (async () => {
      await loadMemoires();
      try {
        const info = await fetchEgliseInfo();                  
        if (info?.eglise?.id) {
          const list = await fetchAspirantsByEglise(info.eglise.id);
          setAspirants(list || []);
        }
      } catch (e) {
        console.error("Erreur chargement aspirants/église :", e);
      }
    })();
  }, []);

  const loadMemoires = async () => {
    setErrorMsg("");
    try {
      const { data } = await api.get("/memoires");
      setMemoires(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur chargement mémoires :", err);
      setErrorMsg("Impossible de charger les mémoires.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    if (!selectedAspirantId) {
      setErrorMsg("Veuillez sélectionner un élève.");
      return;
    }
    if (!file) {
      setErrorMsg("Veuillez sélectionner un fichier (PDF/JPG/PNG).");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("aspirant_id", String(selectedAspirantId));
      if (title.trim()) formData.append("title", title.trim());

      await api.post("/memoires", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccessMsg("Mémoire soumis avec succès !");
      setFile(null);
      setTitle("");
      setSelectedAspirantId("");
      await loadMemoires();
    } catch (err) {
      console.error("Erreur lors de l'envoi :", err);
      setErrorMsg(err.response?.data?.error || "Échec de l’envoi du mémoire.");
    } finally {
      setLoading(false);
    }
  };

  const badgeClass = (status) =>
    status === "valide" ? "bg-success" :
    status === "en_attente" ? "bg-warning" : "bg-danger";

  const buildViewerUrl = (filePath) => {
    const raw = String(filePath || "");
    const b64 = window
      .btoa(unescape(encodeURIComponent(raw)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    return `/dashboard-eglise/viewerEglise?path=${encodeURIComponent(b64)}`;
  };
  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="text-primary fw-bold mb-0">Mémoires des aspirants</h4>
        <span className="badge bg-info text-dark">Total : {memoires.length}</span>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-light">
          <h5 className="mb-0 text-primary">Soumettre un mémoire</h5>
        </div>
        <div className="card-body">
          {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}
          {successMsg && <div className="alert alert-success">{successMsg}</div>}

          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Élève</label>
              <select
                className="form-select"
                value={selectedAspirantId}
                onChange={(e) => setSelectedAspirantId(e.target.value)}
                required
              >
                <option value="">— Sélectionner un élève —</option>
                {aspirants.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} {a.email ? `— ${a.email}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">Titre (optionnel)</label>
              <input
                type="text"
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex: Mémoire d'investiture"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Fichier (PDF / JPG / PNG)</label>
              <input
                type="file"
                className="form-control"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                required
              />
            </div>

            <div className="col-12">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className="bi bi-upload me-2" />
                    Envoyer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {memoires.length === 0 ? (
        <div className="alert alert-info shadow-sm">
          <i className="bi bi-info-circle me-2" />
          Aucun mémoire trouvé.
        </div>
      ) : (
        <div className="row">
          {memoires.map((m) => {
            const submitted = m.submitted_at ? new Date(m.submitted_at).toLocaleString() : "—";
            const fileUrl = getFrontFileUrl(`memoires/${m.file_path}`);

            return (
              <div className="col-md-6 col-lg-6 mb-4" key={m.id}>
                <div className="card h-100 shadow-sm border-0">
                  <div className="card-body">
                    <h5 className="card-title text-primary d-flex align-items-center justify-content-between">
                      <span>
                        <i className="bi bi-file-earmark-text me-2" />
                        {m.title || "Mémoire"}
                        {m.aspirant_name ? ` — ${m.aspirant_name}` : ""}
                      </span>
                      <span className={`badge ${badgeClass(m.status)}`}>{m.status}</span>
                    </h5>
                    <p className="mb-2">
                      <strong>Soumis le :</strong> {submitted}
                    </p>
                    <a className="btn btn-outline-primary w-100">
                      <i className="bi bi-box-arrow-down me-2" />
                      {/* Télécharger le mémoire */}
                        {m.file_path ? (
                            <a
                              href={buildViewerUrl(m.file_path)}
                              target="_blank"
                              rel="noreferrer"
                              title="Ouvrir dans la visionneuse"
                            >
                              Télécharger le mémoire
                            </a>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
